/**
 * The #8 debug bundle — device state a developer can act on, and nothing more.
 *
 * This is an ALLOWLIST, not a filter, and that direction is the whole design.
 * A denylist ("strip the token, strip the blobs") fails open: the next field
 * added to an outbox record ships to a support inbox by default and nobody
 * notices. Every value here is named explicitly, so adding one is a decision
 * someone made on purpose.
 *
 * WHAT IS DELIBERATELY ABSENT:
 *   * blob bytes and media of any kind — size, and a jobsite photo is the
 *     tenant's content, not a diagnostic;
 *   * transcripts, anchor text, room names, owners, rationales — the words a
 *     super said on site can carry names and client detail, and a support
 *     channel is the wrong place for them (Red chose the specced scope over the
 *     easier-to-diagnose variant that included failing records' text);
 *   * the bearer token, and anything derived from it.
 * What ships instead is the SHAPE of the problem: how many records, in what
 * states, how many retries, what the last error code was. That is what would
 * have identified every defect found in the 2026-07-27 sweep.
 *
 * Rule §4 also makes this an affirmative act: the bundle is built only when a
 * human taps "Send debug logs", never attached silently to a feedback submit.
 */

import { recentConsoleEntries, type ConsoleEntry } from "./console-buffer";
import type { OutboxRecord } from "../outbox/types";

export interface OutboxRecordSummary {
  capture_id: string;
  state: string;
  retryCount: number;
  createdAt: string;
  /** Presence only — never the bytes, never the count of pixels. */
  hasBlobs: boolean;
  partCount: number;
  partsDelivered: number;
  /** The rejection CODE (e.g. "skill_route_conflict"), not the server message,
   * which can echo payload fragments back. */
  rejectionCode?: string;
}

export interface Diagnostics {
  app: {
    bundle: string;
    version: string;
    ts: string;
  };
  serviceWorker: {
    supported: boolean;
    hasController: boolean;
    waiting: boolean;
    updateReady: boolean;
  };
  outbox: {
    total: number;
    byState: Record<string, number>;
    maxRetryCount: number;
    records: OutboxRecordSummary[];
  };
  context: {
    tenant_id: string;
    project_ref: string | null;
    platform: string;
    userAgent: string;
    online: boolean;
    storagePersisted: boolean | null;
  };
  console: ConsoleEntry[];
}

/** Cap the per-record list so a device with a large backlog cannot produce a
 * multi-megabyte report. The aggregate counts above stay complete, so the
 * summary is still accurate even when the detail is trimmed. */
const MAX_RECORDS = 50;

/** The running bundle's filename, which is what makes a report actionable at
 * all: several builds have been in the field at once, and "the app is broken"
 * without a bundle id has repeatedly cost a whole re-diagnosis. Read from the
 * loaded module script rather than a build-time constant so it reports what is
 * ACTUALLY executing — the stale-service-worker class of bug is precisely when
 * those two disagree. */
export function currentBundle(): string {
  if (typeof document === "undefined") return "unknown";
  const scripts = Array.from(document.querySelectorAll("script[src]"));
  for (const s of scripts) {
    const src = (s as HTMLScriptElement).src || "";
    const m = /\/(index-[A-Za-z0-9_-]+\.js)/.exec(src);
    if (m) return m[1];
  }
  return "unknown";
}

export function summarizeRecord(r: OutboxRecord): OutboxRecordSummary {
  return {
    capture_id: r.capture_id,
    state: r.state,
    retryCount: r.retryCount,
    createdAt: r.createdAt,
    // Media PRESENCE and per-part delivery progress — the two facts that
    // distinguish "stuck uploading a photo" from "stuck on the commit". Never
    // the media_ref bytes, never the blob itself.
    hasBlobs: (r.envelope?.media?.length ?? 0) > 0,
    partCount: r.envelope?.media?.length ?? 0,
    partsDelivered: Object.values(r.parts_delivered ?? {}).filter(Boolean).length,
    ...(r.rejection ? { rejectionCode: r.rejection.code } : {}),
  };
}

export interface DiagnosticsInput {
  records: OutboxRecord[];
  tenant_id: string;
  project_ref: string | null;
  updateReady: boolean;
  version?: string;
}

export async function buildDiagnostics(input: DiagnosticsInput): Promise<Diagnostics> {
  const byState: Record<string, number> = {};
  let maxRetryCount = 0;
  for (const r of input.records) {
    byState[r.state] = (byState[r.state] ?? 0) + 1;
    if (r.retryCount > maxRetryCount) maxRetryCount = r.retryCount;
  }

  const swSupported = typeof navigator !== "undefined" && "serviceWorker" in navigator;
  let hasController = false;
  let waiting = false;
  if (swSupported) {
    hasController = Boolean(navigator.serviceWorker.controller);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      waiting = Boolean(reg?.waiting);
    } catch {
      // A registration lookup that throws is itself unremarkable; the flags
      // above still describe the state we could observe.
    }
  }

  let storagePersisted: boolean | null = null;
  try {
    if (typeof navigator !== "undefined" && navigator.storage?.persisted) {
      storagePersisted = await navigator.storage.persisted();
    }
  } catch {
    storagePersisted = null;
  }

  return {
    app: {
      bundle: currentBundle(),
      version: input.version ?? "0.1.0",
      ts: new Date().toISOString(),
    },
    serviceWorker: {
      supported: swSupported,
      hasController,
      waiting,
      updateReady: input.updateReady,
    },
    outbox: {
      total: input.records.length,
      byState,
      maxRetryCount,
      records: input.records.slice(0, MAX_RECORDS).map(summarizeRecord),
    },
    context: {
      tenant_id: input.tenant_id,
      project_ref: input.project_ref,
      platform: typeof navigator !== "undefined" ? (navigator.platform ?? "unknown") : "unknown",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      online: typeof navigator !== "undefined" ? navigator.onLine : true,
      storagePersisted,
    },
    console: recentConsoleEntries(),
  };
}
